import { Box, createStyles, Divider, Flex, Group, Stack } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
	apiQueryKey,
	type CatalogRule,
	type PolicyOut,
	useApiListPolicyRules,
} from '@repo/api-codegen';
import { IconButton, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useCatalogList } from '../../../../api/hooks/resourceCatalog';
import entityDrawerStore from '../../../../components/EntityDrawer/store';

import { queryClient } from '../../../../api';
import { resourceCatalogQueryKeyFactory } from '../../../../api/hooks/resourceCatalog/constants';
import { useCustomFilterOptionsConfig } from '../../../../components/Filter/useCustomFilterOptionsConfig';
import SearchBox from '../../../../components/SearchBox/SearchBox';
import { FilterBlockController } from '../../../../features/FilterBlock';
import { useFeatureFlags } from '../../../../utils/featureFlags';
import { getFilterOptionsForPolicy } from '../../../PoliciesCreatePage/utils/policyFilterOptions';
import { ResourceDisplay } from '../../../PoliciesPage/render';
import { getCountDisplay } from '../../../PoliciesPage/utils';
import PolicySection from '../../PolicySection';
import InlineFramework from './InlineFramework';
import InlineOwners from './InlineOwners';
import InlineSeverity from './InlineServerity';
import InlineStatus from './InlineStatus';
import PiiClassifier from './PiiClassifier';

const useStyles = createStyles((theme) => ({
	row: {
		borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		padding: theme.spacing.xs,
		paddingLeft: theme.spacing.sm,
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
		},
	},
	ruleItem: {
		padding: theme.spacing.sm,
		borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		'&:last-child': {
			borderBottom: 'none',
		},
	},
}));

interface PolicyOverviewProps {
	policy: PolicyOut;
	readOnly?: boolean;
}

const PolicyRulesSection = observer(({ policy }: { policy: PolicyOut }) => {
	const { dqsV2, dataScanning } = useFeatureFlags();

	const customOptionsConfig = useCustomFilterOptionsConfig();
	const filterOptions = useMemo(
		() => getFilterOptionsForPolicy(customOptionsConfig, policy.dqs_enabled),
		[customOptionsConfig, policy.dqs_enabled]
	);

	const { data: policyRules } = useApiListPolicyRules({
		pathParams: { policyId: policy.id },
	});

	if (!policyRules?.results?.length) {
		return null;
	}

	return (
		<Stack pb="2xl">
			<Text size="md" weight="bold">
				Conditions
			</Text>
			<Stack w="100%" spacing="md">
				{policyRules.results.map((rule) => {
					if (rule.type === 'PII_SCAN' && dataScanning) {
						return <PiiClassifier key={rule.id} rule={rule} />;
					}

					if (rule.type === 'METADATA' && rule.filter) {
						return (
							<FilterBlockController
								key={rule.id}
								initialRule={rule.filter as CatalogRule}
								filterOptions={filterOptions}
								readOnly
								customRuleText={dqsV2 && rule.name ? rule.name : undefined}
								displayPreview={false}
								footer={
									dqsV2 &&
									(policy as any).dqs_enabled &&
									rule.points_possible && (
										<Group position="apart">
											<Text size="xs">Impact score</Text>
											<Text size="xs">{rule.points_possible}</Text>
										</Group>
									)
								}
							/>
						);
					}

					return null;
				})}
			</Stack>
		</Stack>
	);
});

const PolicyOverview = observer(
	({ policy, readOnly = false }: PolicyOverviewProps) => {
		const { classes } = useStyles();

		const [search, setSearch] = useState('');
		const [debouncedSearch] = useDebouncedValue(search, 100);

		const [page, setPage] = useState(1);

		const customOptionsConfig = useCustomFilterOptionsConfig();
		const filterOptions = useMemo(
			() => getFilterOptionsForPolicy(customOptionsConfig, policy.dqs_enabled),
			[customOptionsConfig, policy.dqs_enabled]
		);

		const { data, isLoading } = useCatalogList({
			filters: {
				filter: policy.resource_rule ?? undefined,
				search_term: debouncedSearch,
			},
			page,
		});

		const handleResourceClick = useCallback(
			(id: string) => {
				entityDrawerStore.openEntityDrawerById(true, id, undefined, () => {
					queryClient.invalidateQueries({
						queryKey: resourceCatalogQueryKeyFactory.allLists(),
					});
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('policy/{policy_id}/issues', {
							policy_id: policy.id,
						}),
					});
				});
			},
			[policy.id]
		);

		return (
			<Stack pt="lg">
				<Stack spacing="xs" pb="lg">
					<InlineStatus policy={policy} />
					<InlineSeverity policy={policy} readOnly={readOnly} />
					<InlineOwners policy={policy} readOnly={readOnly} />
					<InlineFramework policy={policy} readOnly={readOnly} />
				</Stack>
				<PolicyRulesSection policy={policy} />
				<Text size="md" weight="bold">
					Governed resources
				</Text>
				<FilterBlockController
					initialRule={policy.resource_rule}
					filterOptions={filterOptions}
					readOnly
					displayPreview={false}
					ruleName="resource"
					collapsible
				/>
				<PolicySection
					spacing={false}
					content={
						<>
							<Stack p="sm" w="100%">
								<SearchBox
									variant="tertiary"
									placeholder="Search resources"
									onSearch={setSearch}
								/>
							</Stack>
							<Divider />
							<Flex h="50vh" direction="column" sx={{ overflowY: 'auto' }}>
								{data?.results.map((result) => (
									<Box
										key={result.id}
										className={classes.row}
										onClick={() => handleResourceClick(result.id)}
									>
										<ResourceDisplay record={result} />
									</Box>
								))}
							</Flex>
							<Divider />
						</>
					}
					footer={
						<Group position="apart" p="xs" noWrap>
							<Text color="text/secondary/default" size="xs">
								{isLoading
									? 'Loading...'
									: `${(page - 1) * 50 + 1}-${Math.min(page * 50, data?.count ?? 0)} of ${getCountDisplay(data?.count)} results`}
							</Text>
							<Group spacing="xs">
								<IconButton
									disabled={!data?.meta.previous_page}
									variant="tertiary"
									iconName="chevronLeft"
									onClick={() => setPage(page - 1)}
								/>
								<IconButton
									disabled={!data?.meta.next_page}
									variant="tertiary"
									iconName="chevronRight"
									onClick={() => setPage(page + 1)}
								/>
							</Group>
						</Group>
					}
				/>
			</Stack>
		);
	}
);

PolicyOverview.displayName = 'PolicyOverview';
export default PolicyOverview;
